import React, { useState, useEffect, useRef } from 'react';
import { formatUsdt } from '../../utils/format';
import { useContracts } from '../../hooks/useContract';

// Clean, non-confusing earnings display — no ceiling, just what the user earned + breakdown.
// Also shows the user's direct + total team counts below the income tiles.
const EarningsOverview = ({ user, income, userId }) => {
  const { storage } = useContracts();
  const total = user?.totalIncome;

  const [directCount, setDirectCount] = useState(null);
  const [teamCount, setTeamCount] = useState(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) return;
      const myRun = ++runIdRef.current;
      try {
        // Direct = first level of referral tree
        const directList = await storage.getUserReferrals(userId);
        if (cancelled || myRun !== runIdRef.current) return;
        const directIds = directList.map((x) => x.toNumber ? x.toNumber() : Number(x));
        setDirectCount(directIds.length);

        // Team = BFS up to 5 levels (same as TeamCountsCard so numbers match)
        const visited = new Set();
        let frontier = [userId];
        for (let d = 0; d < 5; d++) {
          if (frontier.length === 0) break;
          const results = await Promise.all(frontier.map((id) => storage.getUserReferrals(id).catch(() => [])));
          if (cancelled || myRun !== runIdRef.current) return;
          const next = [];
          for (const r of results) {
            for (const idBn of r) {
              const uid = idBn.toNumber ? idBn.toNumber() : Number(idBn);
              if (!visited.has(uid)) { visited.add(uid); next.push(uid); }
            }
          }
          frontier = next;
        }
        setTeamCount(visited.size);
      } catch (_) { /* ignore */ }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [userId]);

  return (
    <div className="earnings-hero">
      <div className="earnings-hero-glow"></div>

      <div className="earnings-hero-label">
        <span className="pulse-dot"></span>
        Total Earnings
      </div>

      <div className="earnings-hero-amount" style={{ marginBottom: 22 }}>
        <span className="currency">$</span>
        {formatUsdt(total)}
        <span className="denom"> USDT</span>
      </div>

      <div className="earnings-hero-breakdown">
        <div className="ehb-card">
          <div className="ehb-icon" style={{ background: 'linear-gradient(135deg,#22D3EE,#0891B2)' }}>💸</div>
          <div className="ehb-info">
            <div className="ehb-label">Direct Income</div>
            <div className="ehb-value">{formatUsdt(income?.directReferralIncome)}</div>
          </div>
        </div>
        <div className="ehb-card">
          <div className="ehb-icon" style={{ background: 'linear-gradient(135deg,#7C3AED,#5B21B6)' }}>📈</div>
          <div className="ehb-info">
            <div className="ehb-label">Level Income</div>
            <div className="ehb-value">{formatUsdt(income?.levelIncome)}</div>
          </div>
        </div>
        <div className="ehb-card">
          <div className="ehb-icon" style={{ background: 'linear-gradient(135deg,#EC4899,#BE185D)' }}>👑</div>
          <div className="ehb-info">
            <div className="ehb-label">Royalty Income</div>
            <div className="ehb-value">{formatUsdt(income?.royaltyIncome)}</div>
          </div>
        </div>
      </div>

      {/* Team counts row */}
      <div className="earnings-hero-breakdown" style={{ marginTop: 12 }}>
        <div className="ehb-card">
          <div className="ehb-icon" style={{ background: 'linear-gradient(135deg,#7C3AED,#22D3EE)' }}>👤</div>
          <div className="ehb-info">
            <div className="ehb-label">Direct Team</div>
            <div className="ehb-value">{directCount === null ? '…' : directCount}</div>
          </div>
        </div>
        <div className="ehb-card">
          <div className="ehb-icon" style={{ background: 'linear-gradient(135deg,#22D3EE,#EC4899)' }}>🌐</div>
          <div className="ehb-info">
            <div className="ehb-label">Total Team</div>
            <div className="ehb-value">{teamCount === null ? '…' : teamCount}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarningsOverview;
